/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import i18n from '../../i18n';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APICardPoolsResponseInterface,
  CardPool,
  CardPoolsInitialProperties,
  CreateCardPoolPayload,
  LedgerEntry,
  RechargeCardPoolPayload,
  RequestCardPoolsInterface,
  UpdateCardPoolPayload,
} from '../../interface/rfid-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import rfidApi from '../../utils/api/rfid';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Normalizes raw card pool data from API responses
 * @param {any} item - Raw card pool data object
 * @returns {CardPool} Normalized CardPool entity
 */
const formatCardPoolItem = (item: any): CardPool => {
  let balance = 0;
  const rawBalance = item.balanceKwh as any;
  if (typeof rawBalance === 'object' && rawBalance !== null && '$numberDecimal' in rawBalance) {
    balance = Number.parseFloat(rawBalance.$numberDecimal) || 0;
  } else if (typeof rawBalance === 'number') {
    balance = rawBalance;
  } else if (typeof rawBalance === 'string') {
    balance = Number.parseFloat(rawBalance) || 0;
  }

  return {
    ...item,
    id: item.id || item._id || '',
    balanceKwh: balance,
    createdAt: item.createdAt ? ConvertUTCtoDate(item.createdAt) : '',
    updatedAt: item.updatedAt ? ConvertUTCtoDate(item.updatedAt) : '',
  };
};

/**
 * Async thunk to fetch all card pools with query parameters
 */
export const fetchAllCardPools = createAsyncThunk<
  APICardPoolsResponseInterface,
  RequestCardPoolsInterface
>('card-groups/fetchAllCardPools', async (parameters: RequestCardPoolsInterface, { dispatch }) => {
  try {
    const response = await rfidApi.fetchCardPools({
      kind: 'GROUP',
      ...removeEmptyKeys(parameters),
    });
    const rawResults =
      response?.data?.results ||
      response?.data?.data ||
      (Array.isArray(response?.data) ? response?.data : []);

    const formattedList: CardPool[] = Array.isArray(rawResults)
      ? rawResults.map((item: any) => formatCardPoolItem(item))
      : [];

    return {
      results: formattedList,
      limit: response?.data?.limit || parameters.limit || DefaultApiParameters.limit,
      page: response?.data?.page || parameters.page || DefaultApiParameters.page,
      totalPages: response?.data?.totalPages || 0,
      totalResults: response?.data?.totalResults || formattedList.length,
    };
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message || i18n.t('devices:failedToFetchCardGroups'),
        code: error?.response?.data?.code || 500,
        status: true,
      }),
    );
    throw error;
  }
});

/**
 * Async thunk to refresh card pools list
 */
export const refreshCardPools = createAsyncThunk<
  APICardPoolsResponseInterface,
  RequestCardPoolsInterface
>('card-groups/refreshCardPools', async (parameters: RequestCardPoolsInterface) => {
  const response = await rfidApi.fetchCardPools({
    kind: 'GROUP',
    ...removeEmptyKeys(parameters),
  });
  const rawResults =
    response?.data?.results ||
    response?.data?.data ||
    (Array.isArray(response?.data) ? response?.data : []);

  const formattedList: CardPool[] = Array.isArray(rawResults)
    ? rawResults.map((item: any) => formatCardPoolItem(item))
    : [];

  return {
    results: formattedList,
    limit: response?.data?.limit || parameters.limit || DefaultApiParameters.limit,
    page: response?.data?.page || parameters.page || DefaultApiParameters.page,
    totalPages: response?.data?.totalPages || 0,
    totalResults: response?.data?.totalResults || formattedList.length,
  };
});

/**
 * Async thunk to fetch a single card pool by ID
 */
export const fetchCardPoolById = createAsyncThunk<CardPool, string, { rejectValue: string }>(
  'card-groups/fetchCardPoolById',
  async (cardPoolId: string, { rejectWithValue }) => {
    try {
      const response = await rfidApi.fetchCardPoolById(cardPoolId);
      const data = response?.data?.results || response?.data?.data || response?.data || response;
      return formatCardPoolItem({ ...data, id: data?.id || data?._id || cardPoolId });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToFetchCardGroupDetails');
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to create a new card pool
 */
export const createCardPool = createAsyncThunk<
  CardPool,
  CreateCardPoolPayload,
  { rejectValue: string }
>(
  'card-groups/createCardPool',
  async (payload: CreateCardPoolPayload, { dispatch, rejectWithValue }) => {
    try {
      const apiPayload: any = {
        kind: payload.kind || 'GROUP',
        name: payload.name ? payload.name.trim() : '',
        stationIds: payload.stationIds,
      };

      const response = await rfidApi.createCardPool(apiPayload);
      const responseData =
        response?.data?.results || response?.data?.data || response?.data || response;
      const createdPool: CardPool = formatCardPoolItem({
        ...responseData,
        id: responseData.id || responseData._id || `pool_${Date.now()}`,
        tenantId: responseData.tenantId || '',
        kind: responseData.kind || 'GROUP',
        name: responseData.name || payload.name || 'Group Card Pool',
        balanceKwh: typeof responseData.balanceKwh === 'number' ? responseData.balanceKwh : 0,
        stationIds: responseData.stationIds || payload.stationIds,
        tokenCount: responseData.tokenCount || 0,
        createdAt: responseData.createdAt || new Date().toISOString(),
        updatedAt: responseData.updatedAt || new Date().toISOString(),
      });

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'success',
          message: i18n.t('devices:cardPoolCreatedSuccessfully'),
          code: 200,
        }),
      );

      return createdPool;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToCreateCardPool');
      const statusCode = error?.response?.data?.code || error?.response?.status || 500;

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorMessage,
          code: statusCode,
        }),
      );
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to update an existing card pool / group
 */
export const updateCardPool = createAsyncThunk<
  CardPool,
  { cardPoolId: string; data: UpdateCardPoolPayload },
  { rejectValue: string }
>(
  'card-groups/updateCardPool',
  async ({ cardPoolId, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await rfidApi.updateCardPool(cardPoolId, data);
      const responseData =
        response?.data?.results || response?.data?.data || response?.data || response;
      const updatedPool: CardPool = formatCardPoolItem({
        ...responseData,
        id: responseData.id || responseData._id || cardPoolId,
        _id: responseData._id || responseData.id || cardPoolId,
      });

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'success',
          message: i18n.t('devices:cardGroupUpdatedSuccessfully'),
          code: 200,
        }),
      );

      return updatedPool;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToUpdateCardGroup');
      const statusCode = error?.response?.data?.code || error?.response?.status || 500;

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorMessage,
          code: statusCode,
        }),
      );
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to recharge energy balance on a card pool in kWh
 */
export const rechargeCardPool = createAsyncThunk<
  { cardPoolId: string; amountKwh: number; newBalance: number; newLedger: LedgerEntry },
  RechargeCardPoolPayload,
  { rejectValue: string }
>(
  'card-groups/rechargeCardPool',
  async (payload: RechargeCardPoolPayload, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await rfidApi.rechargeCardPool(payload);
      const state = (getState() as any).cardGroups as CardPoolsInitialProperties;
      const targetPool = state?.cardPools?.find((item) => item.id === payload.cardPoolId);
      const previousBalance = targetPool ? targetPool.balanceKwh : 0;
      const newBalance = Number((previousBalance + Number(payload.amountKwh)).toFixed(2));

      const newLedger: LedgerEntry = {
        id: response?.data?.id || response?.data?._id || `led_${Date.now()}`,
        cardPoolId: payload.cardPoolId,
        cardPoolName: targetPool?.name || targetPool?.id || 'Card Pool',
        tokenId: 'MANUAL_RECHARGE',
        tokenUid: 'POOL_TOPUP',
        direction: 'CREDIT',
        amountKwh: Number(payload.amountKwh),
        amountCurrency: null,
        stationId: payload.stationId || targetPool?.stationIds?.[0] || '',
        stationName: 'Direct Tenant Funding',
        sessionId: null,
        cdrReference: null,
        timestamp: new Date().toISOString(),
      };

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'success',
          message: i18n.t('devices:cardPoolSuccessfullyCredited', { amountKwh: payload.amountKwh }),
          code: 200,
        }),
      );

      return {
        cardPoolId: payload.cardPoolId,
        amountKwh: Number(payload.amountKwh),
        newBalance,
        newLedger,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || i18n.t('devices:failedToRechargeCardPool');
      const statusCode = error?.response?.data?.code || error?.response?.status || 500;

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorMessage,
          code: statusCode,
        }),
      );
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to fetch card pool filter options
 */
export const fetchCardPoolFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'card-groups/fetchCardPoolFilters',
  async (_, { dispatch }) => {
    try {
      const response = await rfidApi.fetchCardPoolsFilterList();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || i18n.t('sharedComponent:somethingWentWrong'),
          },
        },
      });
      throw error;
    }
  },
);

/**
 * Compatibility exports matching legacy naming
 */
export const fetchCardPoolsApi = fetchAllCardPools;
export const fetchCardPoolByIdApi = fetchCardPoolById;
export const createCardPoolApi = createCardPool;
export const updateCardPoolApi = updateCardPool;
export const rechargeCardPoolApi = rechargeCardPool;
export const fetchCardPoolFiltersApi = fetchCardPoolFilters;

const availableFilters: CommonFilterProperties[] = [
  {
    name: i18n.t('devices:searchHere'),
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Card Groups / Pools slice
 */
const initialState: CardPoolsInitialProperties = {
  cardPools: [],
  selectedCardPool: null,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  actionLoading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for Card Groups and Pools management
 */
export const cardGroupSlice = createSlice({
  name: 'card-groups',
  initialState,
  reducers: {
    /**
     * Sets selected card pool for side panel or modal
     * @param {CardPoolsInitialProperties} state - Current slice state
     * @param {PayloadAction<CardPool | null>} action - Card pool payload
     */
    setSelectedCardPool: (state, action: PayloadAction<CardPool | null>) => {
      state.selectedCardPool = action.payload;
    },

    /**
     * Updates card pool list filters
     * @param {CardPoolsInitialProperties} state - Current slice state
     * @param {PayloadAction<FilterUpdateProperties>} action - Filter update action
     */
    updateCardPoolFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Disables the filter updated status flag
     * @param {CardPoolsInitialProperties} state - Current slice state
     */
    disableCardPoolFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Sets card pool sorting parameters
     * @param {CardPoolsInitialProperties} state - Current slice state
     * @param {PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>} action - Sorting payload
     */
    setCardPoolSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },

    /**
     * Sets sorting parameters (alias matching other modules)
     * @param {CardPoolsInitialProperties} state - Current slice state
     * @param {PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>} action - Sorting payload
     */
    setSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },

    /**
     * Resets card pool state to initial values
     * @param {CardPoolsInitialProperties} state - Current slice state
     */
    resetCardPoolState: (state) => {
      state.cardPools = [];
      state.selectedCardPool = null;
      state.loading = false;
      state.actionLoading = false;
      state.filters = availableFilters;
      state.isFilterUpdated = false;
      state.page = DefaultApiParameters.page;
      state.limit = DefaultApiParameters.limit;
      state.totalPages = 0;
      state.totalResults = 0;
      state.sortBy = null;
      state.sortField = null;
    },
  },
  /**
   * Extra reducers builder for async thunk actions
   * @param {ActionReducerMapBuilder<CardPoolsInitialProperties>} builder - Redux Toolkit action builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<CardPoolsInitialProperties>) => {
    // Fetch all card pools
    builder
      .addCase(fetchAllCardPools.pending, (state: CardPoolsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllCardPools.fulfilled,
        (
          state: CardPoolsInitialProperties,
          action: PayloadAction<APICardPoolsResponseInterface>,
        ) => {
          state.loading = false;
          state.cardPools = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllCardPools.rejected, (state: CardPoolsInitialProperties) => {
        state.loading = false;
      });

    // Refresh card pools
    builder.addCase(
      refreshCardPools.fulfilled,
      (state: CardPoolsInitialProperties, action: PayloadAction<APICardPoolsResponseInterface>) => {
        state.cardPools = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );

    // Single card pool details fetch by ID
    builder
      .addCase(fetchCardPoolById.pending, (state: CardPoolsInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        fetchCardPoolById.fulfilled,
        (state: CardPoolsInitialProperties, action: PayloadAction<CardPool>) => {
          state.actionLoading = false;
          state.selectedCardPool = action.payload;
          const targetId = action.payload.id || action.payload._id;
          const index = state.cardPools.findIndex(
            (p) => (p.id || p._id) === targetId,
          );
          if (index !== -1) {
            state.cardPools[index] = {
              ...state.cardPools[index],
              ...action.payload,
            };
          }
        },
      )
      .addCase(fetchCardPoolById.rejected, (state: CardPoolsInitialProperties) => {
        state.actionLoading = false;
      });

    // Create Card Pool
    builder
      .addCase(createCardPool.pending, (state: CardPoolsInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        createCardPool.fulfilled,
        (state: CardPoolsInitialProperties, action: PayloadAction<CardPool>) => {
          state.actionLoading = false;
          state.cardPools = [action.payload, ...state.cardPools];
          state.totalResults += 1;
        },
      )
      .addCase(createCardPool.rejected, (state: CardPoolsInitialProperties) => {
        state.actionLoading = false;
      });

    // Update Card Pool
    builder
      .addCase(updateCardPool.pending, (state: CardPoolsInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        updateCardPool.fulfilled,
        (state: CardPoolsInitialProperties, action: PayloadAction<CardPool>) => {
          state.actionLoading = false;
          state.selectedCardPool = action.payload;
          const targetId = action.payload.id || action.payload._id;
          const index = state.cardPools.findIndex(
            (pool) => (pool.id || pool._id) === targetId,
          );
          if (index !== -1) {
            state.cardPools[index] = {
              ...state.cardPools[index],
              ...action.payload,
            };
          }
        },
      )
      .addCase(updateCardPool.rejected, (state: CardPoolsInitialProperties) => {
        state.actionLoading = false;
      });

    // Recharge Card Pool
    builder
      .addCase(rechargeCardPool.pending, (state: CardPoolsInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        rechargeCardPool.fulfilled,
        (
          state: CardPoolsInitialProperties,
          action: PayloadAction<{
            cardPoolId: string;
            amountKwh: number;
            newBalance: number;
            newLedger: LedgerEntry;
          }>,
        ) => {
          state.actionLoading = false;
          const pool = state.cardPools.find((p) => p.id === action.payload.cardPoolId);
          if (pool) {
            pool.balanceKwh = action.payload.newBalance;
            pool.updatedAt = ConvertUTCtoDate(new Date().toISOString());
          }
        },
      )
      .addCase(rechargeCardPool.rejected, (state: CardPoolsInitialProperties) => {
        state.actionLoading = false;
      });

    // Card pool filter options
    builder
      .addCase(fetchCardPoolFilters.pending, (state: CardPoolsInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchCardPoolFilters.fulfilled,
        (state: CardPoolsInitialProperties, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchCardPoolFilters.rejected, (state: CardPoolsInitialProperties) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setSelectedCardPool,
  updateCardPoolFilters,
  disableCardPoolFilterApplied,
  setCardPoolSortData,
  setSortData,
  resetCardPoolState,
} = cardGroupSlice.actions;

export default cardGroupSlice.reducer;
